import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./styles.module.css";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../components/button-v1";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useNavigate } from "react-router-dom";
import { FaEye, FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { updateUserProfile, getUserProfile } from "../../services/user";
import toast from "react-hot-toast";
import Input from "../../components/ui/input";
import DefaultPreferencesFields from "../../components/default-preferences/DefaultPreferencesFields";
import useDefaultPreferences from "../../hooks/useDefaultPreferences";
const defaultValues = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  primary_phone: "",
};

const schema = z
  .object({
    first_name: z
      .string()
      .optional()
      .or(z.string().min(2, "First name is required")),
    last_name: z
      .string()
      .optional()
      .or(z.string().min(2, "Last name is required")),
    email: z.string().optional().or(z.string().email("Invalid email address")),
    password: z
      .string()
      .optional()
      .or(z.string().min(8, "Password must be at least 6 characters")),
    confirm_password: z
      .string()
      .optional()
      .or(z.string().min(8, "Confirm Password must be at least 6 characters")),
    primary_phone: z
      .string()
      .optional()
      .or(
        z
          .string()
          .min(10, "Phone number is required")
          .max(15, "Invalid phone number")
          .regex(/^\d+$/, "Phone number must contain only digits")
      ),
  })
  .refine(
    (data) => {
      // Only validate password match if both password fields are provided
      if (data.password || data.confirm_password) {
        return data.password === data.confirm_password;
      }
      return true;
    },
    {
      message: "Passwords do not match",
      path: ["confirm_password"],
    }
  );

const AddOrganization = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem("user"));
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  // Fetch the profile details
  const userProfileQuery = useQuery(["user-profile"], () => getUserProfile());

  // SCRUM-992: the user's own default account / location / landing page.
  // Not shown to patients.
  const defaults = useDefaultPreferences({
    roleSlug: user?.role_slug,
    enabled: !!user && user.role_slug !== "patient",
  });

  // SCRUM-992: the defaults go in the same profile PUT, which saves all or
  // nothing. The response carries the saved default_preferences, and a 400
  // lists any default_* errors at the top level.
  const updateProfileMutation = useMutation((data) => updateUserProfile(data), {
    onSuccess: (response) => {
      defaults.applySaved(response?.default_preferences);
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      defaults.applyErrors(error?.response?.data);
      toast.error("Could not update profile");
    },
  });

  const onSubmit = (data) => {
    defaults.applyErrors(null);
    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      primary_phone: data.primary_phone,
      // Include password fields only if they are not empty
      ...(data.password && { password: data.password }),
      ...(data.confirm_password && { confirm_password: data.confirm_password }),
      ...defaults.getPayload(),
    };

    updateProfileMutation.mutate(payload);
  };

  useEffect(() => {
    if (userProfileQuery.isLoading) return;
    if (userProfileQuery.data) {
      reset(userProfileQuery.data);
    }
  }, [userProfileQuery.data]);

  return (
    <div className={styles.staff_page}>
      <div className={styles.staff_page_container}>
        <div className={styles.patient_wrapper}>
          <div className={styles.top}>
            <p className={styles.heading}>Settings</p>
          </div>

          <div className={styles.form_container}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.form_step}>
                <h3 className={styles.form_title}>Profile</h3>
                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>First Name</label>
                    <input type="text" {...register("first_name")} />
                    {errors.first_name && (
                      <span className={styles.error}>
                        {errors.first_name.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.form_group}>
                    <label>Last Name</label>
                    <input type="text" {...register("last_name")} />
                    {errors.last_name && (
                      <span className={styles.error}>
                        {errors.last_name.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Email</label>
                    <input type="text" {...register("email")} />
                    {errors.email && (
                      <span className={styles.error}>
                        {errors.email.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.form_group}>
                    <label>Phone</label>
                    <Controller
                      name="primary_phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          mask={true}
                          maskPattern="(999) 999-9999"
                          name={field.name}
                          value={field.value || ""}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          className="no-arrows"
                        />
                      )}
                    />
                    {errors.primary_phone && (
                      <span className={styles.error}>
                        {errors.primary_phone.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.form_row}>
                  <div className={styles.form_group}>
                    <label>Password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      readOnly
                      data-lpignore="true"
                      data-1p-ignore="true"
                      onFocus={(e) => e.target.removeAttribute("readonly")}
                      {...register("password")}
                    />
                    {errors.password && (
                      <span className={styles.error}>
                        {errors.password.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.form_group}>
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      readOnly
                      data-lpignore="true"
                      data-1p-ignore="true"
                      onFocus={(e) => e.target.removeAttribute("readonly")}
                      {...register("confirm_password")}
                    />
                    {errors.confirm_password && (
                      <span className={styles.error}>
                        {errors.confirm_password.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* SCRUM-992: saved with the profile by the button below. */}
              {defaults.isAvailable && (
                <div className={styles.form_step}>
                  <h3 className={styles.form_second_title}>Login Defaults</h3>
                  <DefaultPreferencesFields
                    defaults={defaults}
                    rowClassName={styles.form_row}
                    fieldClassName={styles.form_group}
                  />
                </div>
              )}

              <div className={styles.button_group}>
                <Button
                  loading={updateProfileMutation.isLoading}
                  onClick={() => handleSubmit(onSubmit)}
                  type="submit"
                >
                  Update & Save
                </Button>
              </div>
            </form>
          </div>
          {/* </div> */}
        </div>
      </div>
    </div>
  );
};

export default AddOrganization;
